// src/App.jsx — top-level router
import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, Link } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { useTranslation } from "react-i18next";
import Navbar from "./components/Navbar";
import PrivateRoute from "./components/PrivateRoute";

import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import CreateTontine from "./pages/CreateTontine";
import TontineDetails from "./pages/TontineDetails";
import AdminPanel from "./pages/AdminPanel";
import Home from "./pages/Home";
import Circles from "./pages/Circles";
import Join from "./pages/Join";
import Pricing from "./pages/Pricing";
import FAQ from "./pages/FAQ";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Referral from "./pages/Referral";
import HelpCenter from "./pages/HelpCenter";
import PaymentPage from "./pages/PaymentPage";
import { useAuth } from "./context/AuthContext";
import { ArrowUpRight, Heart, Mail, MapPin, MessageCircle, Phone, ShieldCheck } from "lucide-react";

function HomeRoute() {
  const { user } = useAuth();
  return user ? <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace /> : <Home />;
}

function App() {
  return (
    <AuthProvider>
      <AppLayout />
    </AuthProvider>
  );
}

function AppLayout() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [theme, setTheme] = useState(() => localStorage.getItem("itontine-theme") || "sunrise");
  useEffect(() => {
    document.documentElement.dataset.theme = theme === "midnight" ? "midnight" : "sunrise";
    localStorage.setItem("itontine-theme", theme);
  }, [theme]);
  return (
      <BrowserRouter>
        <div className="min-h-screen bg-paper">
          <Navbar theme={theme} onThemeChange={() => setTheme((current) => current === "midnight" ? "sunrise" : "midnight")} />
          <Routes>
            <Route path="/" element={<HomeRoute />} />
            <Route path="/circles" element={<Circles />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/join" element={<Join />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/help" element={<HelpCenter />} />
            <Route path="/referral" element={<PrivateRoute role="member"><Referral /></PrivateRoute>} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/register" element={<Navigate to="/join" replace />} />

            <Route
              path="/dashboard"
              element={
                <PrivateRoute role="member">
                  <Dashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/tontines"
              element={
                <PrivateRoute role="member">
                  <Dashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/tontines/mine"
              element={
                <PrivateRoute role="member">
                  <Dashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/tontines/create"
              element={
                <PrivateRoute role="member">
                  <CreateTontine />
                </PrivateRoute>
              }
            />
            <Route
              path="/tontines/:id/pay"
              element={
                <PrivateRoute role="member">
                  <PaymentPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/tontines/:id"
              element={<TontineDetails />}
            />
            <Route
              path="/admin"
              element={
                <PrivateRoute role="admin">
                  <AdminPanel />
                </PrivateRoute>
              }
            />

            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          {!user || user.role !== "admin" ? <footer className="border-t border-forest/20 bg-[#10231c] text-green-100">
            <div className="mx-auto grid max-w-6xl gap-10 px-4 py-14 sm:px-6 md:grid-cols-2 lg:grid-cols-[1.3fr_0.8fr_0.8fr_1fr] lg:px-8">
              <div>
                <Link to="/" className="font-serif text-2xl font-bold text-white"><span className="italic text-gold-400">i</span>Tontine</Link>
                <p className="mt-4 max-w-sm text-sm leading-7 text-green-100/75">{t("footer.description")}</p>
                <div className="mt-6 space-y-3 text-sm text-green-100/80"><a className="flex items-center gap-2 transition hover:text-gold-300" href="mailto:support@itontine.com"><Mail size={15} /> support@itontine.com</a><a className="flex items-center gap-2 transition hover:text-gold-300" href="tel:+14388556120"><Phone size={15} /> +1 (438) 855-6120</a><span className="flex items-center gap-2"><MapPin size={15} /> {t("footer.serviceArea")}</span></div>
                <a href="https://wa.me/14388556120" target="_blank" rel="noreferrer" className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#25D366] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#1ebe5d]"><MessageCircle size={18} /> {t("footer.whatsapp")}</a>
              </div>
              <div><p className="text-xs font-bold uppercase tracking-[0.18em] text-gold-300">{t("footer.explore")}</p><div className="mt-5 space-y-3 text-sm"><NavigateLink to="/">{t("nav.home")}</NavigateLink><NavigateLink to="/circles">{t("footer.savingsCircles")}</NavigateLink><NavigateLink to="/pricing">{t("footer.simulator")}</NavigateLink><NavigateLink to="/help">{t("footer.help")} <ArrowUpRight size={13} /></NavigateLink></div></div>
              <div><p className="text-xs font-bold uppercase tracking-[0.18em] text-gold-300">{t("footer.account")}</p><div className="mt-5 space-y-3 text-sm"><NavigateLink to="/join">{t("footer.join")}</NavigateLink><NavigateLink to="/login">{t("nav.login")}</NavigateLink><NavigateLink to="/dashboard">{t("nav.dashboard")}</NavigateLink><NavigateLink to="/referral">{t("footer.rewards")}</NavigateLink></div></div>
              <div><p className="text-xs font-bold uppercase tracking-[0.18em] text-gold-300">{t("footer.trust")}</p><div className="mt-5 space-y-3 text-sm"><NavigateLink to="/help">{t("footer.support")}</NavigateLink><NavigateLink to="/terms">{t("footer.terms")}</NavigateLink><NavigateLink to="/privacy">{t("footer.privacy")}</NavigateLink><span className="flex items-center gap-2 pt-2 text-green-100/70"><ShieldCheck size={14} className="text-gold-400" /> {t("footer.secure")}</span><span className="flex items-center gap-2 text-green-100/70"><Heart size={14} className="text-gold-400" /> {t("footer.community")}</span></div></div>
            </div>
            <div className="mx-auto max-w-6xl border-t border-white/10 px-4 py-7 sm:px-6 lg:px-8"><div className="flex flex-col justify-between gap-3 text-xs text-green-100/60 sm:flex-row"><span>© 2026 iTontine. All rights reserved.</span><span>Stripe · PayPal · GDPR-ready data practices</span></div></div>
          </footer> : null}
          {!user || user.role !== "admin" ? <a href="https://wa.me/14388556120" target="_blank" rel="noreferrer" aria-label="Chat with iTontine on WhatsApp" className="fixed bottom-5 right-5 z-30 flex h-14 w-14 items-center justify-center rounded-full bg-forest text-gold-300 shadow-lg transition hover:scale-105 hover:bg-forest-deep">
            <MessageCircle size={26} />
          </a> : null}
        </div>
      </BrowserRouter>
  );
}

function NavigateLink({ to, children }) {
  return <Link to={to} className="flex w-fit items-center gap-1 transition-colors hover:text-gold-400">{children}</Link>;
}

export default App;
